import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';
import { GetAppliedNode, GetDefinitionNode } from './testUtil';

type Result = ReturnType<typeof useSeedByEnvironment>[0];

export type SeedResponse = NonNullable<Result['data']>;

export type RequestedSeedAppliedNode = GetAppliedNode<'seeds', SeedResponse>;

export type RequestedSeedDefinitionNode = GetDefinitionNode<'seeds', SeedResponse>;

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetSeedByUniqueId = graphql(/* GraphQL */ `
  query Seed($environmentId: BigInt!, $uniqueIds: [String!], $userId: BigInt) {
    environment(id: $environmentId, userId: $userId) {
      adapterType
      applied {
        lastUpdatedAt
        seeds(first: 1, filter: { uniqueIds: $uniqueIds }) {
          edges {
            node {
              fqn
              name
              uniqueId
              tags
              description
              packageName
              filePath
              patchPath
              database
              schema
              meta
              alias
              projectId
              executionInfo {
                lastRunId
                lastRunStatus
                executeCompletedAt
                lastJobDefinitionId
                executionTime
              }
              children {
                uniqueId
              }
              tests {
                uniqueId
                name
                columnName
                executionInfo {
                  lastRunStatus
                }
                testMetadata {
                  name
                  kwargs
                }
              }
              catalog {
                type
                columns {
                  type
                  name
                  comment
                  tags
                  description
                  descriptionOriginColumnName
                  descriptionOriginResourceUniqueId
                  meta
                }
                primaryKey
                rowCountStat
                bytesStat
                stats {
                  id
                  label
                  value
                  description
                  include
                }
              }
            }
          }
        }
      }
    }
  }
`);

type SeedByEnvironmentParams = {
  environmentId: number | undefined;
  userId: number | null;
  uniqueId: string;
  pause?: boolean;
};

export const useSeedByEnvironment = ({
  environmentId = 0,
  userId,
  uniqueId,
  pause,
}: SeedByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetSeedByUniqueId,
    variables: { environmentId, uniqueIds: [uniqueId], userId },
    pause: pause || !environmentId || !uniqueId,
  });

  useCacheMonitor({
    applied: query[0].data?.environment.applied?.lastUpdatedAt,
  });

  return query;
};
